import { useCallback, useMemo } from 'react'

import type { QuoteResult } from 'components/quoting/types'
import { fullStateNameFromAbbreviation } from 'utils'
import { totalCumulativeAllowanceByState } from 'utils/quote_calculations'

export const COLOR_BUCKETS: string[] = [
  '#DBB6FF',
  '#9C3BFF',
  '#832ED9',
  '#6720B0',
  '#4B1377',
]
export const NO_DATA_COLOR = '#E5E5E5'

export function useMapHelpers (
  quoteResults: QuoteResult[] | undefined,
  employerEmployeeContribution: number,
  employerSpouseContribution: number,
  employerChildContribution: number
) {
  const cumulativeAllowanceByState = useMemo(() => {
    return totalCumulativeAllowanceByState(quoteResults || [], {
      employerEmployeeContribution,
      employerSpouseContribution,
      employerChildContribution
    })
  }, [quoteResults, employerEmployeeContribution, employerSpouseContribution, employerChildContribution])

  const { stateRankings } = useMemo(() => {
    if (!quoteResults) return { stateRankings: {} }
    const allowances = Object.entries(cumulativeAllowanceByState)
      .sort(([, a], [, b]) => (a as number) - (b as number))
      .map(([state], index) => ({ state, rank: index }))
    const rankings = Object.fromEntries(allowances.map(({ state, rank }) => [state, rank]))
    return { stateRankings: rankings }
  }, [cumulativeAllowanceByState, quoteResults])

  const getColorForState = useCallback((stateName: string): string => {
    const totalStates = Object.keys(stateRankings).length
    if (totalStates === 0) return NO_DATA_COLOR
    const stateRank = stateRankings[stateName]
    if (stateRank === undefined) return NO_DATA_COLOR
    const bucketSize = totalStates / COLOR_BUCKETS.length
    const bucketIndex = Math.min(Math.floor(stateRank / bucketSize), COLOR_BUCKETS.length - 1)
    return COLOR_BUCKETS[bucketIndex]
  }, [stateRankings])

  const getAllowanceRangeForBucketIndex = useCallback((bucketIndex: number): [number | null, number | null] => {
    const totalStates = Object.keys(stateRankings).length
    const bucketSize = totalStates / COLOR_BUCKETS.length
    const sortedAllowances = Object.values(cumulativeAllowanceByState).sort((a, b) => a - b)
    const lowerRank = Math.floor(bucketIndex * bucketSize)
    const upperRank = Math.floor((bucketIndex + 1) * bucketSize)
    const lowerValue = sortedAllowances[lowerRank]
    const upperValue = sortedAllowances[Math.min(upperRank, sortedAllowances.length - 1)]
    return [
      bucketIndex === 0 ? null : lowerValue,
      bucketIndex === COLOR_BUCKETS.length - 1 ? null : upperValue,
    ]
  }, [stateRankings, cumulativeAllowanceByState])

  const getStatesInBucketIndex = useCallback((bucketIndex: number): string[] => {
    const [lowerRank, upperRank] = getAllowanceRangeForBucketIndex(bucketIndex)
    return Object.keys(cumulativeAllowanceByState).filter((stateName) => {
      if (lowerRank && upperRank) {
        return cumulativeAllowanceByState[stateName] >= lowerRank && cumulativeAllowanceByState[stateName] < upperRank
      } else if (lowerRank) {
        return cumulativeAllowanceByState[stateName] >= lowerRank
      } else {
        return cumulativeAllowanceByState[stateName] < (upperRank as number)
      }
    })
  }, [cumulativeAllowanceByState, getAllowanceRangeForBucketIndex])

  const employeeCountByState = useMemo(() => {
    if (!quoteResults) return {}
    const employeeCountByState = quoteResults.reduce((acc, result) => {
      const state = fullStateNameFromAbbreviation(result.rating_area_code.substring(0, 2))
      if (!state) return acc
      acc[state] = (acc[state] || 0) + 1
      return acc
    }, {} as Record<string, number>)
    return employeeCountByState
  }, [quoteResults])

  const totalCoveredLivesByState = useMemo(() => {
    if (!quoteResults) return {}
    const totalCoveredLivesByState = quoteResults.reduce((acc, result) => {
      const state = fullStateNameFromAbbreviation(result.rating_area_code.substring(0, 2))
      if (!state) return acc
      acc[state] = (acc[state] || 0) + (result.dependents.length + 1)
      return acc
    }, {} as Record<string, number>)
    return totalCoveredLivesByState
  }, [quoteResults])

  return {
    cumulativeAllowanceByState,
    stateRankings,
    getColorForState,
    getAllowanceRangeForBucketIndex,
    getStatesInBucketIndex,
    employeeCountByState,
    totalCoveredLivesByState,
  }
}
